// src/pages/users/users-list.jsx
import { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SecondaryButton } from "../../components/form/secondary-button";
import { useAppLoading } from "../../components/layout/app-loading-provider";
import { PortalMainLayout } from "../../components/layout/portal-main-layout";
import { CognitoContext } from "../../contexts/cognito/cognito-provider";
import { CoreServiceSelector } from "../../services/core/service-selector";
import { consumePrefetch, prefetchUserRecord } from "../../services/core/users-prefetch";
import { AuthorisedPage } from "../auth/authorised-page";

const StatusBadge = ({ type }) => {
  const base = "px-3 py-1 rounded-full text-sm font-semibold";
  switch (type) {
    case "active":
      return <span className={`${base} bg-green-500 text-black`}>Active</span>;
    case "blocked":
      return <span className={`${base} bg-gray-500 text-white`}>Blocked</span>;
    case "invited":
      return <span className={`${base} bg-red-500 text-white`}>Invited</span>;
    default:
      return null;
  }
};

const RoleBadge = ({ role }) => {
  return (
    <span className="px-3 py-1 rounded-full text-sm font-semibold bg-blue text-white">
      {role}
    </span>
  );
};

const UserCard = ({ user, service }) => {
  let status = "invited";
  if (user.LastAccessDate) status = "active";
  else if (user.IsBlocked) status = "blocked";

  const roles = user.UserRoles?.split(",") || [];
  const role = roles.includes("useradmin") ? "Admin" : "";

  return (
    <Link
      target="_self"
      to={{ pathname: `/edit-user/${user.Id}` }}
      onMouseEnter={() => service && prefetchUserRecord(service, user.Id)}
    >
      <div className="bg-background-secondary rounded-2xl px-3 py-[12px] justify-between flex flex-row gap-4 ring-1 ring-[#34353F] hover:scale-[1.02] transition-transform duration-200 hover:cursor-pointer relative">
        <div className="flex flex-col">
          <span className="font-headings-primary font-medium text-white text-base">
            {user.FullName}
          </span>
          <span className="text-base font-body-primary text-white/90">{user.Email}</span>
          <span className="text-base font-body-primary text-white/90">{user.CompanyName}</span>
        </div>
        {/* Badges at top right */}
        <div className="absolute top-3 right-3 flex gap-2 items-center">
          {role && <RoleBadge role={role} />}
          <StatusBadge type={status} />
        </div>
      </div>
    </Link>
  );
};

export const UsersListPage = () => {
  const [data, setData] = useState([]);
  const { showLoading, hideLoading } = useAppLoading();
  const [pageState, setPageState] = useState({ isLoading: true });
  const [unauthorised, setUnauthorised] = useState({ show: false });
  const { state } = useContext(CognitoContext);
  const serviceRef = useRef(null);

  const loadUsers = async () => {
    const service = CoreServiceSelector.GetUserManagementService(state.session);
    if (!service) return;
    serviceRef.current = service;

    setPageState({ ...pageState, isLoading: true });
    showLoading();

    const prefetched = consumePrefetch();
    const result = await (prefetched ?? service.getUsersList()).catch((err) => {
      if (err?.response?.status === 401) setUnauthorised({ show: true });
      return null;
    });

    setData(result || []);
    setPageState({ ...pageState, isLoading: false });
    hideLoading();
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <AuthorisedPage>
      <PortalMainLayout isUnauthorised={unauthorised.show}>
        <div className="bg-black fixed left-1/2 -translate-x-1/2 top-0 z-50 h-20 !rounded-xl md:ml-[2.5rem] flex items-center w-full px-4 justify-between">
          <div className="md:pl-20">
            <Link to="/my-profile" className="flex items-center text-white ">
              <i className="bi bi-chevron-left"></i>
              <span className="text-white font-headings-primary ml-2 text-xl">
                Manage users
              </span>
            </Link>
          </div>
          <Link to="/add-user" className="md:pr-8 ">
            <SecondaryButton className="px-4 py-2">New user</SecondaryButton>
          </Link>
        </div>

        <div className="w-full md:w-3/4 max-w-[640px] mx-auto flex flex-col !gap-[16px] mb-[200px] mt-[6rem]">
          {data.map((user) => (
            <UserCard key={user.Id} user={user} service={serviceRef.current} />
          ))}
        </div>
      </PortalMainLayout>
    </AuthorisedPage>
  );
};
